import { create } from 'zustand';
import { AssistantThinkingState } from '../types/conversation.types';

/**
 * The answer Viktor is writing right now, if any.
 *
 * One at a time: every admin has exactly one Viktor thread, and a new question
 * while Viktor is still writing INTERRUPTS the current answer rather than
 * queueing behind it (the interrupted answer is not stored, and Viktor answers
 * both questions together). So there is never more than one run to track.
 *
 * Written by the socket, which is the only thing that learns when a run starts
 * and ends, and read by the composer to swap Send for Stop. Deliberately not
 * persisted: the stream exists nowhere on the server, so after a reload the
 * honest state is "not writing" - the finished answer arrives as a message
 * either way.
 *
 * Kept beside `useChatSocketState` rather than inside it for the same reason
 * that store is not inside `useAuth`: a burst of deltas must only re-render
 * what reads them.
 */

export type ViktorRun = {
  conversationId: number;
  runId: string;
  /** Stop has been pressed; the button waits for `viktor_run_finished`. */
  cancelling: boolean;
  /**
   * Picked up from a delta rather than from `viktor_run_started` - the page
   * was reloaded, or the socket rebuilt, while Viktor was already writing.
   * The beginning of the answer is gone for good, so its text is not shown:
   * the run is tracked for the indicator and the Stop button, and the answer
   * arrives whole as `new_message`.
   */
  adopted: boolean;
};

type ViktorRunState = {
  run: ViktorRun | null;
  /**
   * What Viktor is doing this second (`assistant_thinking`), or null when he
   * is doing nothing worth saying.
   *
   * Beside the run rather than inside it because the event carries no `runId`
   * and because the two do not begin together: the phase can change several
   * times inside one answer, and the run is what decides whether there is
   * anywhere to show it. Every way a run ends clears it with the run, so the
   * words can never outlive the bubble they sit in.
   */
  phase: AssistantThinkingState | null;
  setPhase: (phase: AssistantThinkingState | null) => void;
  /** `viktor_run_started`. Replaces whatever was running - see above. */
  startRun: (conversationId: number, runId: string, adopted?: boolean) => void;
  /** `viktor_run_finished`, or the watchdog giving up on it. */
  finishRun: (runId: string) => void;
  setCancelling: (runId: string, cancelling: boolean) => void;
  /** A reconnect or a sign-out: nothing that was running can be trusted. */
  reset: () => void;
};

export const useViktorRunState = create<ViktorRunState>()((set) => ({
  run: null,
  phase: null,

  setPhase: (phase) => set({ phase }),

  /* A fresh run starts with nothing to say: the phase of the answer this one
     replaced would otherwise stand until the new `reading` lands. */
  startRun: (conversationId, runId, adopted = false) =>
    set({
      run: { conversationId, runId, cancelling: false, adopted },
      phase: null,
    }),

  /* Keyed on the run so a late `finished` for an interrupted answer cannot
     clear the run that replaced it. */
  finishRun: (runId) =>
    set((state) =>
      state.run?.runId === runId ? { run: null, phase: null } : state
    ),

  setCancelling: (runId, cancelling) =>
    set((state) =>
      state.run?.runId === runId ? { run: { ...state.run, cancelling } } : state
    ),

  reset: () => set({ run: null, phase: null }),
}));
